import React from 'react';
import { BAR_BANDS, COMPONENT_COUNT } from '../shared/earningsSetup';
import { TEXT_MUTED, TEXT_SUBTLE } from '../constants/uiColors';

// The Expectations Bar meter (issue #98): score, fill bar, rating, input
// coverage and the implied move. ONE component for the stock table's cell and
// the stock profile's Company Snapshot (issue #445), so the two can never
// draw the same score differently.

/**
 * Expectations Bar fill colours, one per `BAR_BANDS` entry in the same
 * ascending order. Cool -> warm rather than red/green: the score is descriptive
 * and makes no claim about which way the stock will move, so a red/green scale
 * would read as a bad/good call it is not making.
 *
 * Indexed off the shared bands rather than restating their thresholds, so the
 * colour can never disagree with the rating label the worker computed.
 */
const EXPECTATIONS_BAR_RAMP = ["#3b82f6", "#60a5fa", "#9ca3af", "#fb923c", "#ea580c"];
const expectationsBarColor = (score) =>
  EXPECTATIONS_BAR_RAMP[BAR_BANDS.findIndex((band) => score < band.maxExclusive)];

/**
 * Spells out what the ±x% caption is, because the number invites two wrong
 * readings: that it is a forecast direction, and that it is always an
 * *earnings* move. It is neither — it is an empirically calibrated
 * one-standard-deviation band (issue #107) around whatever expiry
 * `options_snapshots` happens to hold for the ticker, which is the post-print
 * expiry only for stocks actually near earnings.
 */
const IMPLIED_MOVE_TOOLTIP =
  "Expected move in either direction, derived from option prices and calibrated " +
  "against ~1,700 actual earnings reactions so that about 2 in 3 land inside the " +
  "band (a true 1-sigma). Not a direction. For stocks near earnings the underlying " +
  "expiry is the one after the print; otherwise it is simply the next expiry on " +
  "file, and the figure is not earnings-specific.";

// Inline styles: `text-sm` and the grey text utilities are ABSENT from the
// prebuilt `src/tailwind-output.css`, and a class that was never generated
// fails silently (CLAUDE.md).
const STYLES = {
  cell: { fontSize: "0.875rem", minWidth: "96px" },
  empty: { fontSize: "0.875rem", color: TEXT_MUTED },
  row: { display: "flex", alignItems: "center", gap: "6px" },
  score: { fontWeight: 600 },
  track: {
    flex: 1,
    height: "4px",
    borderRadius: "2px",
    background: "#e5e7eb",
    overflow: "hidden",
  },
  caption: { fontSize: "0.75rem", color: TEXT_SUBTLE },
  muted: { color: TEXT_MUTED },
};

const ExpectationsBarMeter = ({ score, rating, coverage, impliedMove }) => {
  // Null means no component was available at all — render an explicit
  // no-data state. A score of 0 is a real reading ("washed out") and
  // must not be swallowed by a falsy check.
  if (score == null) {
    return <span style={STYLES.empty}>—</span>;
  }

  return (
    <div style={STYLES.cell}>
      <div style={STYLES.row}>
        <span style={STYLES.score}>{score}</span>
        <span style={STYLES.track}>
          <span
            style={{
              display: "block",
              width: `${score}%`,
              height: "100%",
              background: expectationsBarColor(score),
            }}
          />
        </span>
      </div>
      <div style={STYLES.caption}>
        {rating}
        {coverage < COMPONENT_COUNT && (
          <span
            style={STYLES.muted}
            title={`Only ${coverage} of ${COMPONENT_COUNT} inputs available for this stock`}
          >
            {` (${coverage}/${COMPONENT_COUNT})`}
          </span>
        )}
      </div>
      {impliedMove != null && (
        <div style={STYLES.caption} title={IMPLIED_MOVE_TOOLTIP}>
          {`±${impliedMove.toFixed(1)}% est`}
        </div>
      )}
    </div>
  );
};

export default ExpectationsBarMeter;
